/**
 * AuditLog Page - Tabellen-basiertes Audit Log (Variante A)
 * Shows all actions across spaces and instances with filtering and CSV export.
 * Transport entries can be expanded to show BPMN diff and file-level diff visualization.
 */
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { FONT, MONO } from "../theme/tokens";
import { useAppTheme } from "../theme/ThemeContext";
import { api, v, getToken } from "../utils/helpers";
import { useToast, ToastContainer } from "../components/ui";
import IflowDiagram from "../components/IflowDiagram";
import AuditChangeReport from "../components/AuditChangeReport";

// LCS-based unified diff algorithm
function computeUnifiedDiff(sourceText,targetText,contextLines=3){
  const linesA=(sourceText||"").split("\n");
  const linesB=(targetText||"").split("\n");
  const n=linesA.length,m=linesB.length;
  // For very large files, fall back to greedy approach
  if(n>2000||m>2000)return computeGreedyDiff(linesA,linesB,contextLines);
  // Build LCS table (optimized: only store two rows)
  const prev=new Array(m+1).fill(0);
  const curr=new Array(m+1).fill(0);
  // We need to trace back, so store the full table for files up to 2000 lines
  const dp=[];
  for(let i=0;i<=n;i++){dp[i]=new Array(m+1).fill(0);}
  for(let i=1;i<=n;i++){
    for(let j=1;j<=m;j++){
      if(linesA[i-1]===linesB[j-1])dp[i][j]=dp[i-1][j-1]+1;
      else dp[i][j]=Math.max(dp[i-1][j],dp[i][j-1]);
    }
  }
  // Trace back to build edit script
  const ops=[];
  let i=n,j=m;
  while(i>0||j>0){
    if(i>0&&j>0&&linesA[i-1]===linesB[j-1]){
      ops.push({type:"unchanged",text:linesA[i-1],lineOld:i,lineNew:j});
      i--;j--;
    }else if(j>0&&(i===0||dp[i][j-1]>=dp[i-1][j])){
      ops.push({type:"add",text:linesB[j-1],lineNew:j});
      j--;
    }else{
      ops.push({type:"remove",text:linesA[i-1],lineOld:i});
      i--;
    }
  }
  ops.reverse();
  // Group into hunks with context lines
  return groupIntoHunks(ops,contextLines);
}

function computeGreedyDiff(linesA,linesB,contextLines){
  const ops=[];
  let ia=0,ib=0;
  while(ia<linesA.length||ib<linesB.length){
    if(ia<linesA.length&&ib<linesB.length&&linesA[ia]===linesB[ib]){
      ops.push({type:"unchanged",text:linesA[ia],lineOld:ia+1,lineNew:ib+1});
      ia++;ib++;
    }else{
      let foundInB=-1;
      for(let j=ib+1;j<Math.min(ib+30,linesB.length);j++){
        if(linesA[ia]&&linesB[j]&&linesA[ia]===linesB[j]){foundInB=j;break;}
      }
      let foundInA=-1;
      for(let j=ia+1;j<Math.min(ia+30,linesA.length);j++){
        if(linesB[ib]&&linesA[j]&&linesB[ib]===linesA[j]){foundInA=j;break;}
      }
      if(foundInB>=0&&(foundInA<0||foundInB-ib<=foundInA-ia)){
        while(ib<foundInB){ops.push({type:"add",text:linesB[ib],lineNew:ib+1});ib++;}
      }else if(foundInA>=0){
        while(ia<foundInA){ops.push({type:"remove",text:linesA[ia],lineOld:ia+1});ia++;}
      }else{
        if(ia<linesA.length){ops.push({type:"remove",text:linesA[ia],lineOld:ia+1});ia++;}
        if(ib<linesB.length){ops.push({type:"add",text:linesB[ib],lineNew:ib+1});ib++;}
      }
    }
  }
  return groupIntoHunks(ops,contextLines);
}

function groupIntoHunks(ops,contextLines){
  // Find ranges of changed lines
  const changes=[];
  for(let i=0;i<ops.length;i++){
    if(ops[i].type!=="unchanged")changes.push(i);
  }
  if(changes.length===0)return{hunks:[],hasChanges:false};
  // Group changes into hunks (merge if within 2*contextLines of each other)
  const groups=[];
  let start=changes[0],end=changes[0];
  for(let i=1;i<changes.length;i++){
    if(changes[i]-end<=contextLines*2+1){end=changes[i];}
    else{groups.push([start,end]);start=changes[i];end=changes[i];}
  }
  groups.push([start,end]);
  const hunks=[];
  for(const[gs,ge] of groups){
    const hunkStart=Math.max(0,gs-contextLines);
    const hunkEnd=Math.min(ops.length-1,ge+contextLines);
    const lines=[];
    for(let i=hunkStart;i<=hunkEnd;i++){lines.push(ops[i]);}
    hunks.push({lines,startOld:ops[hunkStart]?.lineOld||1,startNew:ops[hunkStart]?.lineNew||1});
  }
  return{hunks,hasChanges:true};
}

// Simple line-based diff for BPMN (backward compat)
function computeLineDiff(textA, textB){
  const linesA=(textA||"").split("\n");
  const linesB=(textB||"").split("\n");
  const result=[];
  let ia=0,ib=0;
  while(ia<linesA.length||ib<linesB.length){
    if(ia<linesA.length&&ib<linesB.length&&linesA[ia].trim()===linesB[ib].trim()){
      result.push({type:"same",text:linesA[ia]});ia++;ib++;
    }else{
      let foundInB=-1;
      for(let j=ib+1;j<Math.min(ib+20,linesB.length);j++){
        if(linesA[ia]&&linesB[j]&&linesA[ia].trim()===linesB[j].trim()){foundInB=j;break;}
      }
      let foundInA=-1;
      for(let j=ia+1;j<Math.min(ia+20,linesA.length);j++){
        if(linesB[ib]&&linesA[j]&&linesB[ib].trim()===linesA[j].trim()){foundInA=j;break;}
      }
      if(foundInB>=0&&(foundInA<0||foundInB-ib<=foundInA-ia)){
        while(ib<foundInB){result.push({type:"added",text:linesB[ib]});ib++;}
      }else if(foundInA>=0){
        while(ia<foundInA){result.push({type:"removed",text:linesA[ia]});ia++;}
      }else{
        if(ia<linesA.length){result.push({type:"removed",text:linesA[ia]});ia++;}
        if(ib<linesB.length){result.push({type:"added",text:linesB[ib]});ib++;}
      }
    }
  }
  return result;
}

// Syntax highlighting for diff lines
const GROOVY_KW=/\b(def|return|if|else|for|while|try|catch|throw|new|import|class|null|true|false|void|in|as|extends|implements|interface|abstract|final|static|private|public|protected)\b/g;
const GROOVY_CPI=/\b(message|body|headers|properties|exchange)\b/g;
const XML_TAG=/<\/?[\w:.-]+[\s>]/g;
const XML_ATTR=/\s([\w:.-]+)=/g;

function highlightLine(text,lang){
  if(!text)return text;
  if(lang==="groovy"||lang==="javascript"){
    // String literals
    const parts=[];
    let last=0;
    const strRe=/(["'])(?:(?!\1|\\).|\\.)*\1/g;
    const commentRe=/\/\/.*/g;
    let m;
    // Check for line comment
    const commentIdx=text.indexOf("//");
    if(commentIdx>=0&&(text.indexOf('"')===-1||commentIdx<text.indexOf('"'))){
      const before=text.substring(0,commentIdx);
      const comment=text.substring(commentIdx);
      return <><span>{highlightGroovyTokens(before)}</span><span style={{color:"var(--text-3)",fontStyle:"italic"}}>{comment}</span></>;
    }
    return <span>{highlightGroovyTokens(text)}</span>;
  }
  if(lang==="xml"||lang==="xslt"){
    return <span>{highlightXmlTokens(text)}</span>;
  }
  return text;
}

function highlightGroovyTokens(text){
  const result=[];
  let remaining=text;
  let key=0;
  // Simple tokenizer: split by word boundaries and highlight keywords
  const re=/(\b(?:def|return|if|else|for|while|try|catch|throw|new|import|class|null|true|false|void|in|as|extends|implements|interface|abstract|final|static|private|public|protected)\b)|(\b(?:message|body|headers|properties|exchange)\b)|((?:["'])(?:(?!["'\\]).|\\.)*(?:["']))/g;
  let lastIdx=0;
  let m;
  while((m=re.exec(text))!==null){
    if(m.index>lastIdx)result.push(<span key={key++}>{text.slice(lastIdx,m.index)}</span>);
    if(m[1])result.push(<span key={key++} style={{color:"#c678dd",fontWeight:600}}>{m[1]}</span>);
    else if(m[2])result.push(<span key={key++} style={{color:"#e5c07b"}}>{m[2]}</span>);
    else if(m[3])result.push(<span key={key++} style={{color:"#98c379"}}>{m[3]}</span>);
    lastIdx=re.lastIndex;
  }
  if(lastIdx<text.length)result.push(<span key={key++}>{text.slice(lastIdx)}</span>);
  return result;
}

function highlightXmlTokens(text){
  const result=[];
  let key=0;
  const re=/(<\/?)([\w:.-]+)((?:\s+[\w:.-]+="[^"]*")*\s*\/?>?)|([^<]+)/g;
  let m;
  let lastIdx=0;
  while((m=re.exec(text))!==null){
    if(m.index>lastIdx)result.push(<span key={key++}>{text.slice(lastIdx,m.index)}</span>);
    if(m[4]){
      result.push(<span key={key++}>{m[4]}</span>);
    }else{
      result.push(<span key={key++} style={{color:"var(--text-3)"}}>{m[1]}</span>);
      result.push(<span key={key++} style={{color:"#e06c75"}}>{m[2]}</span>);
      if(m[3]){
        // Parse attributes
        const attrStr=m[3];
        const attrParts=[];
        let ak=0;
        const ar=/\s([\w:.-]+)(="[^"]*")/g;
        let ai=0,am;
        while((am=ar.exec(attrStr))!==null){
          if(am.index>ai)attrParts.push(<span key={ak++}>{attrStr.slice(ai,am.index)}</span>);
          attrParts.push(<span key={ak++}> <span style={{color:"#d19a66"}}>{am[1]}</span><span style={{color:"#98c379"}}>{am[2]}</span></span>);
          ai=ar.lastIndex;
        }
        if(ai<attrStr.length)attrParts.push(<span key={ak++} style={{color:"var(--text-3)"}}>{attrStr.slice(ai)}</span>);
        result.push(<span key={key++}>{attrParts}</span>);
      }
    }
    lastIdx=re.lastIndex;
  }
  if(lastIdx<text.length)result.push(<span key={key++}>{text.slice(lastIdx)}</span>);
  return result;
}

// Skeleton loader component
function SkeletonLoader(){
  const barStyle=(w,d)=>({
    height:12,width:w,background:"var(--surface-2)",borderRadius:4,
    animation:`skeleton-pulse 1.5s ease-in-out ${d}s infinite`,marginBottom:8
  });
  return <div style={{padding:16}}>
    <style>{`@keyframes skeleton-pulse{0%,100%{opacity:.4}50%{opacity:.8}}`}</style>
    <div style={barStyle("60%",0)}/>
    <div style={barStyle("80%",0.15)}/>
    <div style={barStyle("45%",0.3)}/>
    <div style={barStyle("70%",0.45)}/>
  </div>;
}

// File diff viewer for a single file
function FileDiffBlock({file,defaultOpen}){
  const{t}=useTranslation(["auditlog","gemeinsam"]);
  const[open,setOpen]=useState(defaultOpen||false);
  const statusColors={modified:{bg:"rgba(59,142,216,0.12)",color:"var(--accent)",label:"M"},
    added:{bg:"rgba(0,200,100,0.12)",color:"#2dd477",label:"A"},
    removed:{bg:"rgba(200,0,0,0.12)",color:"#f87171",label:"D"}};
  const sc=statusColors[file.type]||statusColors.modified;
  const shortPath=file.path.replace(/^src\/main\/resources\//,"");
  const diffResult=open&&file.type==="modified"?computeUnifiedDiff(file.sourceContent,file.targetContent,file.language==="xml"?5:3):null;
  const addedOnlyLines=open&&file.type==="added"?(file.targetContent||"").split("\n"):null;
  const removedOnlyLines=open&&file.type==="removed"?(file.sourceContent||"").split("\n"):null;

  return <div style={{border:"1px solid var(--border-subtle)",borderRadius:6,marginBottom:6,overflow:"hidden"}}>
    <div onClick={()=>setOpen(!open)} style={{display:"flex",alignItems:"center",gap:8,padding:"6px 10px",
      cursor:"pointer",background:open?"var(--surface-1)":"var(--surface-0)",fontSize:11,
      borderBottom:open?"1px solid var(--border-subtle)":"none"}}
      onMouseEnter={e=>{if(!open)e.currentTarget.style.background="var(--surface-1)";}}
      onMouseLeave={e=>{if(!open)e.currentTarget.style.background="var(--surface-0)";}}>
      <span style={{fontSize:9,fontWeight:700,padding:"1px 4px",borderRadius:3,background:sc.bg,color:sc.color,fontFamily:MONO}}>{sc.label}</span>
      <span style={{fontFamily:MONO,fontSize:10,color:"var(--text-1)",flex:1,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{shortPath}</span>
      <span style={{fontSize:9,color:"var(--text-3)",flexShrink:0}}>
        {file.additions>0&&<span style={{color:"#2dd477"}}>+{file.additions}</span>}
        {file.additions>0&&file.deletions>0&&" "}
        {file.deletions>0&&<span style={{color:"#f87171"}}>-{file.deletions}</span>}
      </span>
      <span style={{fontSize:10,color:"var(--text-3)",transform:open?"rotate(90deg)":"rotate(0deg)",transition:"transform .15s"}}>&#9654;</span>
    </div>
    {open&&<div style={{maxHeight:500,overflow:"auto",background:"var(--surface-0)"}}>
      {file.type==="modified"&&diffResult&&(diffResult.hasChanges?
        <pre style={{fontFamily:MONO,fontSize:10,lineHeight:1.6,margin:0,padding:0}}>
          {diffResult.hunks.map((hunk,hi)=><div key={hi}>
            {hi>0&&<div style={{padding:"2px 10px",color:"var(--text-3)",background:"var(--surface-2)",fontSize:9,fontFamily:MONO,textAlign:"center",borderTop:"1px solid var(--border-subtle)",borderBottom:"1px solid var(--border-subtle)"}}>...</div>}
            {hunk.lines.map((line,li)=>{
              const isAdd=line.type==="add";
              const isRem=line.type==="remove";
              const bg=isAdd?"rgba(0,200,100,0.08)":isRem?"rgba(200,0,0,0.08)":"transparent";
              const color=isAdd?"#2dd477":isRem?"#f87171":"var(--text-2)";
              const prefix=isAdd?"+":isRem?"-":" ";
              const lineNum=isAdd?(line.lineNew||""):isRem?(line.lineOld||""):(line.lineOld||"");
              return <div key={`${hi}-${li}`} style={{background:bg,padding:"0 8px",minHeight:18,display:"flex"}}>
                <span style={{color:"var(--text-3)",userSelect:"none",width:36,flexShrink:0,textAlign:"right",paddingRight:8,fontSize:9,lineHeight:"18px",opacity:.6}}>{lineNum}</span>
                <span style={{color:line.type==="unchanged"?"var(--text-3)":color,userSelect:"none",width:14,flexShrink:0,textAlign:"center",fontWeight:600}}>{prefix}</span>
                <span style={{color:isAdd||isRem?color:"var(--text-2)",whiteSpace:"pre-wrap",wordBreak:"break-all",flex:1}}>{highlightLine(line.text,file.language)}</span>
              </div>;
            })}
          </div>)}
        </pre>
        :<div style={{padding:12,fontSize:11,color:"var(--text-3)"}}>{t("dateien.nurWhitespace")}</div>
      )}
      {file.type==="added"&&addedOnlyLines&&<pre style={{fontFamily:MONO,fontSize:10,lineHeight:1.6,margin:0,padding:0}}>
        {addedOnlyLines.map((line,i)=><div key={i} style={{background:"rgba(0,200,100,0.08)",padding:"0 8px",minHeight:18,display:"flex"}}>
          <span style={{color:"var(--text-3)",userSelect:"none",width:36,flexShrink:0,textAlign:"right",paddingRight:8,fontSize:9,lineHeight:"18px",opacity:.6}}>{i+1}</span>
          <span style={{color:"#2dd477",userSelect:"none",width:14,flexShrink:0,textAlign:"center",fontWeight:600}}>+</span>
          <span style={{color:"#2dd477",whiteSpace:"pre-wrap",wordBreak:"break-all",flex:1}}>{highlightLine(line,file.language)}</span>
        </div>)}
      </pre>}
      {file.type==="removed"&&removedOnlyLines&&<pre style={{fontFamily:MONO,fontSize:10,lineHeight:1.6,margin:0,padding:0}}>
        {removedOnlyLines.map((line,i)=><div key={i} style={{background:"rgba(200,0,0,0.08)",padding:"0 8px",minHeight:18,display:"flex"}}>
          <span style={{color:"var(--text-3)",userSelect:"none",width:36,flexShrink:0,textAlign:"right",paddingRight:8,fontSize:9,lineHeight:"18px",opacity:.6}}>{i+1}</span>
          <span style={{color:"#f87171",userSelect:"none",width:14,flexShrink:0,textAlign:"center",fontWeight:600}}>-</span>
          <span style={{color:"#f87171",whiteSpace:"pre-wrap",wordBreak:"break-all",flex:1}}>{highlightLine(line,file.language)}</span>
        </div>)}
      </pre>}
    </div>}
  </div>;
}

// Parse parameters.prop file into key-value map
function parseParametersProps(text){
  if(!text)return {};
  const result={};
  for(const line of text.split("\n")){
    const trimmed=line.trim();
    if(!trimmed||trimmed.startsWith("#"))continue;
    const eqIdx=trimmed.indexOf("=");
    if(eqIdx<0)continue;
    result[trimmed.slice(0,eqIdx)]=trimmed.slice(eqIdx+1);
  }
  return result;
}

// Externalized Parameters diff section
function ParametersDiffSection({files}){
  const{t}=useTranslation(["auditlog","gemeinsam"]);
  const paramFile=files.find(f=>f.path==="parameters.prop"||f.path==="src/main/resources/parameters.prop");
  if(!paramFile||paramFile.type==="unchanged")return null;

  const before=parseParametersProps(paramFile.sourceContent);
  const after=parseParametersProps(paramFile.targetContent);
  const allKeys=new Set([...Object.keys(before),...Object.keys(after)]);
  const rows=[];
  for(const k of allKeys){
    const bv=before[k]!==undefined?before[k]:null;
    const av=after[k]!==undefined?after[k]:null;
    if(bv===av)continue;
    let status="changed";
    if(bv===null)status="added";
    else if(av===null)status="removed";
    rows.push({key:k,before:bv,after:av,status});
  }
  if(rows.length===0)return null;

  const statusStyles={
    added:{bg:`${v("success")}14`,color:v("success")},
    removed:{bg:`${v("danger")}14`,color:v("danger")},
    changed:{bg:`${v("warning")}14`,color:v("warning")}
  };

  return <div style={{marginBottom:12,border:"1px solid var(--border)",borderRadius:8,overflow:"hidden"}}>
    <div style={{padding:"8px 12px",background:"var(--surface-2)",fontSize:11,fontWeight:600,color:"var(--text-1)",
      display:"flex",alignItems:"center",gap:6,borderBottom:"1px solid var(--border-subtle)"}}>
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 01-2.83 2.83l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z"/></svg>
      {t("parameter.titel")}
      <span style={{fontSize:10,padding:"1px 6px",borderRadius:4,background:"var(--accent)",color:"#fff",fontWeight:700}}>{rows.length}</span>
    </div>
    <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:MONO}}>
      <thead><tr style={{background:"var(--surface-1)"}}>
        <th style={{textAlign:"left",padding:"5px 10px",color:"var(--text-3)",fontWeight:600,fontSize:9,borderBottom:"1px solid var(--border-subtle)"}}>{t("gemeinsam:bauteil.vergleich.spalteVorher")}</th>
        <th style={{textAlign:"left",padding:"5px 10px",color:"var(--text-3)",fontWeight:600,fontSize:9,borderBottom:"1px solid var(--border-subtle)"}}>{t("parameter.spalte")}</th>
        <th style={{textAlign:"left",padding:"5px 10px",color:"var(--text-3)",fontWeight:600,fontSize:9,borderBottom:"1px solid var(--border-subtle)"}}>{t("gemeinsam:bauteil.vergleich.spalteNachher")}</th>
      </tr></thead>
      <tbody>{rows.map(r=>{
        const s=statusStyles[r.status];
        return <tr key={r.key} style={{background:s.bg,borderBottom:"1px solid var(--border-subtle)"}}>
          <td style={{padding:"4px 10px",color:r.before===null?"var(--text-3)":"var(--text-2)",wordBreak:"break-all"}}>{r.before===null?"-":r.before||t("gemeinsam:bauteil.vergleich.leer")}</td>
          <td style={{padding:"4px 10px",color:s.color,fontWeight:600,wordBreak:"break-all"}}>{r.key}</td>
          <td style={{padding:"4px 10px",color:r.after===null?"var(--text-3)":"var(--text-2)",wordBreak:"break-all"}}>{r.after===null?"-":r.after||t("gemeinsam:bauteil.vergleich.leer")}</td>
        </tr>;
      })}</tbody>
    </table>
  </div>;
}

// Changed Files tab content
function ChangedFilesViewer({entryId,meta,diffCache,setDiffCache,selectedArtId:propArtId}){
  const{t}=useTranslation(["auditlog","gemeinsam"]);
  const[selectedArtId,setSelectedArtId]=useState(propArtId||null);
  const cacheKey=selectedArtId?`${entryId}_${selectedArtId}`:entryId;
  const cached=diffCache[cacheKey];
  const[data,setData]=useState(cached||null);
  const[loading,setLoading]=useState(!cached);
  const[error,setError]=useState(null);

  useEffect(()=>{
    if(cached){setData(cached);setLoading(false);return;}
    setLoading(true);setError(null);
    const url=selectedArtId?`/api/audit-log/${entryId}/diff?artifactId=${encodeURIComponent(selectedArtId)}`:`/api/audit-log/${entryId}/diff`;
    api(url).then(d=>{
      // Auto-select first artifact for package transports
      if(!selectedArtId&&d.artifacts?.length&&!d.available){
        setSelectedArtId(d.artifacts[0]);return;
      }
      setData(d);setDiffCache(prev=>({...prev,[cacheKey]:d}));setLoading(false);
    }).catch(e=>{setError(e.message||t("dateien.ladefehler"));setLoading(false);});
  },[entryId,selectedArtId]);

  if(loading)return <SkeletonLoader/>;
  if(error)return <div style={{padding:16,color:"var(--danger)",fontSize:11}}>{t("gemeinsam:bauteil.vergleich.fehler",{meldung:error})}</div>;

  // Artifact selector for package transports
  const artifacts=data?.artifacts||[];
  const ArtifactSelector=artifacts.length>1?<div style={{marginBottom:10}}>
    <span style={{fontSize:10,color:"var(--text-3)",marginRight:6}}>{t("vergleich.artefakt")}</span>
    <select value={selectedArtId||artifacts[0]} onChange={e=>{setSelectedArtId(e.target.value);setData(null);setLoading(true);}}
      style={{fontSize:10,background:"var(--surface-1)",color:"var(--text-1)",border:"1px solid var(--border)",borderRadius:4,padding:"2px 6px",fontFamily:FONT}}>
      {artifacts.map(id=><option key={id} value={id}>{id}</option>)}
    </select>
  </div>:null;

  if(!data||!data.available)return <div style={{padding:16}}>
    {ArtifactSelector}
    <span style={{color:"var(--text-3)",fontSize:11}}>{data?.reason||t("dateien.keinDiff")}</span>
  </div>;

  const{files,summary,isNewArtifact}=data;
  const changedFiles=files.filter(f=>f.type!=="unchanged");

  if(changedFiles.length===0)return <div style={{padding:16}}>
    {ArtifactSelector}
    <span style={{color:"var(--text-3)",fontSize:11}}>{t("dateien.keineUnterschiede")}</span>
  </div>;

  return <div>
    {ArtifactSelector}
    {/* Externalized Parameters diff (prominent, at top) */}
    <ParametersDiffSection files={files}/>
    {/* Summary header */}
    <div style={{display:"flex",alignItems:"center",gap:12,padding:"8px 0",marginBottom:8,fontSize:11,color:"var(--text-2)"}}>
      <span style={{fontWeight:600}}>{t("dateien.geaendert",{count:changedFiles.length})}</span>
      <span style={{fontSize:10,color:"var(--text-3)"}}>
        {summary.totalAdditions>0&&<span style={{color:"#2dd477"}}>+{summary.totalAdditions}</span>}
        {summary.totalAdditions>0&&summary.totalDeletions>0&&" "}
        {summary.totalDeletions>0&&<span style={{color:"#f87171"}}>-{summary.totalDeletions}</span>}
      </span>
      {isNewArtifact&&<span style={{fontSize:10,padding:"1px 6px",borderRadius:4,background:`${v("success")}14`,color:v("success"),fontWeight:600}}>{t("dateien.neuesArtefakt")}</span>}
    </div>
    {/* File diff blocks */}
    {changedFiles.map((f,i)=><FileDiffBlock key={f.path} file={f} defaultOpen={i===0&&changedFiles.length<=5}/>)}
  </div>;
}


// Extract BPMN element IDs + fingerprints for diffing (with ifl:property details)
function parseBpmnElements(xmlStr){
  try{
    const p=new DOMParser();const doc=p.parseFromString(xmlStr,"application/xml");
    const res={};
    const tags=["serviceTask","scriptTask","callActivity","CallActivity","task","receiveTask","sendTask",
      "exclusiveGateway","inclusiveGateway","parallelGateway","eventBasedGateway",
      "startEvent","endEvent","intermediateCatchEvent","intermediateThrowEvent","subProcess","participant","messageFlow"];
    for(const tag of tags){
      const els=doc.getElementsByTagNameNS("*",tag);
      for(const el of els){
        const id=el.getAttribute("id");
        if(!id)continue;
        const name=el.getAttribute("name")||"";
        // FIX: parse ifl:property with child <key> and <value> elements
        const properties={};
        const propEls=el.getElementsByTagNameNS("*","property");
        for(const pe of propEls){
          const keyEl=pe.getElementsByTagNameNS("*","key")[0];
          const valEl=pe.getElementsByTagNameNS("*","value")[0];
          const k=keyEl?keyEl.textContent.trim():pe.getAttribute("key")||"";
          const v2=valEl?valEl.textContent.trim():pe.getAttribute("value")||"";
          if(k)properties[k]=v2;
        }
        const bpmnType=el.localName||tag;
        // Build stable fingerprint from properties + name
        const sortedEntries=Object.entries(properties).sort(([a],[b])=>a.localeCompare(b));
        const fingerprint=name+"|"+sortedEntries.map(([k,v2])=>k+"="+v2).join("|");
        // Resolve display type from activityType/subActivityType/cmdVariantUri
        const activityType=properties.activityType||"";
        const subActivityType=properties.subActivityType||"";
        const cmdUri=properties.cmdVariantUri||"";
        const cnameMatch=cmdUri.match(/cname::([^/]+)/);
        const cname=cnameMatch?cnameMatch[1]:"";
        res[id]={name,fingerprint,type:bpmnType,properties,activityType,subActivityType,cname};
      }
    }
    return res;
  }catch{return{};}
}

function diffBpmnElements(before,after){
  const added=[],removed=[],changed=[],unchanged=[];
  const allIds=new Set([...Object.keys(before),...Object.keys(after)]);
  for(const id of allIds){
    if(!before[id])added.push(id);
    else if(!after[id])removed.push(id);
    else if(before[id].fingerprint!==after[id].fingerprint)changed.push(id);
    else unchanged.push(id);
  }
  return{added,removed,changed,unchanged};
}


// Parse Content Modifier row/cell XML (used in both AuditLog and IntegrationContent)
function parseContentModifierXml(val) {
  if (!val || typeof val !== "string") return null;
  // The DOMParser already decodes HTML entities, so val may contain literal <row>...</row>
  if (!val.includes("<row>") && !val.includes("<row ")) return null;
  try {
    const rows = [];
    const rowRe = /<row[^>]*>([\s\S]*?)<\/row>/g;
    let rm;
    while ((rm = rowRe.exec(val)) !== null) {
      const cellRe = /<cell\s+id=["']([^"']*)["'][^>]*>([\s\S]*?)<\/cell>|<cell\s+id=["']([^"']*)["'][^>]*\/>/g;
      const row = {};
      let cm;
      while ((cm = cellRe.exec(rm[1])) !== null) {
        const key = cm[1] || cm[3];
        const v = cm[2] !== undefined ? cm[2] : "";
        if (key) row[key] = v;
      }
      if (Object.keys(row).length) rows.push(row);
    }
    if (!rows.length) return null;
    const ORDER = ["Action","Type","Name","Value","Default","Datatype"];
    const cols = [...new Set([...ORDER, ...rows.flatMap(r => Object.keys(r))])].filter(k => rows.some(r => k in r));
    return { rows, cols };
  } catch { return null; }
}

// Element inspect popup - Glass-4D styled
function ElementInspectPopup({element,side,parsedBefore,parsedAfter,diffIds,onClose,scripts}){
  const{theme}=useAppTheme();
  const{t}=useTranslation(["auditlog","gemeinsam"]);
  const isDark=theme==="dark";
  if(!element)return null;
  const elId=element.id||element;
  const info=side==="before"?parsedBefore[elId]:parsedAfter[elId];
  if(!info)return null;

  let status=t("gemeinsam:bauteil.vergleich.elementStatus.unveraendert"),statusColor="var(--text-3)",statusBg="var(--surface-2)";
  if(diffIds){
    if(diffIds.added.includes(elId)){status=t("gemeinsam:bauteil.vergleich.elementStatus.neu");statusColor="var(--iio-dia-diff-add)";statusBg="var(--iio-bg-2)";}
    else if(diffIds.removed.includes(elId)){status=t("gemeinsam:bauteil.vergleich.elementStatus.entfernt");statusColor="var(--iio-dia-diff-del)";statusBg="var(--iio-bg-2)";}
    else if(diffIds.changed.includes(elId)){status=t("gemeinsam:bauteil.vergleich.elementStatus.geaendert");statusColor="var(--iio-dia-diff-chg)";statusBg="var(--iio-bg-2)";}
  }

  // BPMN-Typnamen bleiben stehen: sie heissen in der Integration Suite in beiden
  // Sprachen so, uebersetzt findet der Nutzer sie in SAP nicht wieder.
  const typeLabels={serviceTask:"Service Task",scriptTask:"Script Task",callActivity:"Call Activity",
    exclusiveGateway:"Router (Exclusive)",inclusiveGateway:"Inclusive Gateway",parallelGateway:"Parallel Gateway",
    startEvent:"Start Event",endEvent:"End Event",subProcess:"Sub Process",participant:"Participant",
    task:"Task",receiveTask:"Receive Task",sendTask:"Send Task",
    intermediateCatchEvent:"Intermediate Catch Event",intermediateThrowEvent:"Intermediate Throw Event"};
  const typeName=typeLabels[info.type]||info.type;

  let propDiffRows=null;
  if(diffIds&&diffIds.changed.includes(elId)&&parsedBefore[elId]&&parsedAfter[elId]){
    const bProps=parsedBefore[elId].properties||{};
    const aProps=parsedAfter[elId].properties||{};
    const allKeys=new Set([...Object.keys(bProps),...Object.keys(aProps)]);
    propDiffRows=[];
    for(const k of allKeys){
      const bv=bProps[k]!==undefined?bProps[k]:null;
      const av=aProps[k]!==undefined?aProps[k]:null;
      if(bv!==av)propDiffRows.push({key:k,before:bv,after:av});
    }
  }

  return <div style={{
    position:"absolute",top:8,right:8,left:typeof window!=="undefined"&&window.innerWidth<768?8:undefined,width:typeof window!=="undefined"&&window.innerWidth<768?"auto":320,maxHeight:420,overflow:"auto",
    background:isDark?"rgba(12,20,40,0.88)":"rgba(255,255,255,0.92)",
    backdropFilter:"blur(20px)",WebkitBackdropFilter:"blur(20px)",
    border:isDark?"1px solid rgba(45,212,191,0.25)":"1px solid rgba(20,184,166,0.3)",
    borderRadius:10,
    boxShadow:isDark?"0 8px 32px rgba(0,0,0,.5), 0 0 0 1px rgba(45,212,191,.1)":"0 8px 32px rgba(0,0,0,.12)",
    zIndex:20,fontSize:11
  }}>
    <div style={{
      display:"flex",alignItems:"center",justifyContent:"space-between",
      padding:"8px 12px",
      borderBottom:"1px solid var(--border-subtle)",
      background:isDark?"rgba(45,212,191,0.06)":"rgba(20,184,166,0.06)"
    }}>
      <span style={{fontWeight:600,color:"var(--text-1)",fontSize:11}}>{info.name||elId}</span>
      <button onClick={onClose} style={{background:"none",border:"none",cursor:"pointer",
        color:"var(--text-3)",fontSize:16,lineHeight:1,padding:0}}>&times;</button>
    </div>
    <div style={{padding:"8px 12px"}}>
      <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:6,flexWrap:"wrap"}}>
        <span style={{fontSize:10,color:"var(--text-2)",fontFamily:MONO}}>{typeName}</span>
        {(info.activityType||info.subActivityType||info.cname)&&<span style={{fontSize:9,padding:"1px 6px",borderRadius:4,
          background:isDark?"rgba(45,212,191,0.1)":"rgba(20,184,166,0.1)",
          color:isDark?"#2DD4BF":"#14B8A6",fontFamily:MONO}}>{info.activityType||(info.subActivityType||info.cname)}</span>}
        <span style={{fontSize:9,padding:"1px 6px",borderRadius:4,background:statusBg,color:statusColor,fontWeight:600}}>{status}</span>
      </div>
      <div style={{fontSize:9,color:"var(--text-3)",fontFamily:MONO,marginBottom:8}}>ID: {elId}</div>

      {propDiffRows&&propDiffRows.length>0&&<div>
        <div style={{fontSize:9,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>{t("gemeinsam:bauteil.vergleich.geaenderteProperties")}</div>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:10,fontFamily:MONO}}>
          <thead><tr style={{borderBottom:"1px solid var(--border-subtle)"}}>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--text-3)",fontWeight:600,fontSize:9}}>{t("gemeinsam:bauteil.vergleich.spalteProperty")}</th>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--text-3)",fontWeight:600,fontSize:9}}>{t("gemeinsam:bauteil.vergleich.spalteVorher")}</th>
            <th style={{textAlign:"left",padding:"3px 4px",color:"var(--text-3)",fontWeight:600,fontSize:9}}>{t("gemeinsam:bauteil.vergleich.spalteNachher")}</th>
          </tr></thead>
          <tbody>{propDiffRows.map(r=>{
            const renderPropVal = (val, color) => {
              const parsed = parseContentModifierXml(val);
              if (parsed) {
                return (
                  <div style={{fontSize:9, overflow:"auto", maxHeight:120}}>
                    <table style={{borderCollapse:"collapse", width:"100%"}}>
                      <thead><tr>{parsed.cols.map(c=><th key={c} style={{padding:"2px 4px",color:"var(--text-3)",fontWeight:600,textAlign:"left",borderBottom:"1px solid var(--border-subtle)",whiteSpace:"nowrap"}}>{c}</th>)}</tr></thead>
                      <tbody>{parsed.rows.map((row,i)=><tr key={i}>{parsed.cols.map(c=><td key={c} style={{padding:"2px 4px",color:"var(--text-1)",borderBottom:"1px solid var(--border-subtle)",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row[c]||"\u2014"}</td>)}</tr>)}</tbody>
                    </table>
                  </div>
                );
              }
              return <span style={{color}}>{val===null?"\u2014":val||t("gemeinsam:bauteil.vergleich.leer")}</span>;
            };
            return <tr key={r.key} style={{background:"rgba(251,191,36,.08)"}}>
            <td style={{padding:"3px 4px",color:"var(--text-1)",fontWeight:500,maxWidth:100,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{r.key}</td>
            <td style={{padding:"3px 4px",wordBreak:"break-all"}}>{renderPropVal(r.before, r.before===null?("var(--text-3)"):"var(--iio-critical)")}</td>
            <td style={{padding:"3px 4px",wordBreak:"break-all"}}>{renderPropVal(r.after, r.after===null?("var(--text-3)"):"var(--iio-success)")}</td>
          </tr>})}</tbody>
        </table>
      </div>}
      {propDiffRows&&propDiffRows.length===0&&<div style={{fontSize:10,color:"var(--text-3)"}}>{t("gemeinsam:bauteil.vergleich.keinePropertyUnterschiede")}</div>}

      {!propDiffRows&&info.properties&&Object.keys(info.properties).length>0&&<div>
        <div style={{fontSize:9,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>{t("gemeinsam:bauteil.vergleich.properties")}</div>
        <div style={{maxHeight:150,overflow:"auto"}}>
          {Object.entries(info.properties).map(([k,val])=>{
            const parsed = parseContentModifierXml(val);
            return <div key={k} style={{display:"flex",gap:4,fontSize:10,
            padding:"2px 0",borderBottom:"1px solid var(--border-subtle)",flexDirection:parsed?"column":"row"}}>
            <span style={{color:"var(--text-2)",fontFamily:MONO,minWidth:80}}>{k}</span>
            {parsed ? <div style={{fontSize:9, overflow:"auto", maxHeight:120}}>
              <table style={{borderCollapse:"collapse", width:"100%"}}>
                <thead><tr>{parsed.cols.map(c=><th key={c} style={{padding:"2px 4px",color:"var(--text-3)",fontWeight:600,textAlign:"left",borderBottom:"1px solid var(--border-subtle)",whiteSpace:"nowrap"}}>{c}</th>)}</tr></thead>
                <tbody>{parsed.rows.map((row,i)=><tr key={i}>{parsed.cols.map(c=><td key={c} style={{padding:"2px 4px",color:"var(--text-1)",borderBottom:"1px solid var(--border-subtle)",maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{row[c]||"\u2014"}</td>)}</tr>)}</tbody>
              </table>
            </div> : <span style={{color:"var(--text-1)",fontFamily:MONO,wordBreak:"break-all"}}>{val||t("gemeinsam:bauteil.vergleich.leer")}</span>}
          </div>})}
        </div>
      </div>}

      {(info.activityType==="Script"||info.subActivityType==="GroovyScript"||info.subActivityType==="JavaScript"||info.cname==="GroovyScript"||info.properties?.script||info.properties?.scriptFilename||info.properties?.scriptName)&&(()=>{
        // Try all known SAP CPI property keys for the script filename
        const scriptRef=info.properties?.script||info.properties?.scriptFilename||info.properties?.scriptName||"";
        const scriptFileName=scriptRef.split("/").pop();
        // Lookup: exact, then case-insensitive
        let scriptData=scripts?.[scriptFileName];
        if(!scriptData&&scripts){
          const k=Object.keys(scripts).find(k=>k.toLowerCase()===scriptFileName.toLowerCase());
          if(k)scriptData=scripts[k];
        }
        // If still not found, try to find any groovy/js file in scripts
        if(!scriptData&&scripts&&Object.keys(scripts).length>0){
          const k=Object.keys(scripts).find(k=>k.endsWith(".groovy")||k.endsWith(".gsh")||k.endsWith(".js"));
          if(k)scriptData=scripts[k];
        }
        // For "before" panel: fall back to "after" content if no "before" snapshot exists (new artifact)
        const content=side==="before"?(scriptData?.before||scriptData?.after):scriptData?.after;
        const resolvedName=scriptFileName||Object.keys(scripts||{})[0]||"Script";
        if(!content){
          // Show placeholder - script node confirmed but no content captured in snapshot
          return <div style={{marginTop:8}}>
            <div style={{fontSize:9,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>{t("gemeinsam:bauteil.vergleich.script")}</div>
            <div style={{fontSize:9,color:"var(--text-3)",fontStyle:"italic",padding:"4px 0"}}>
              {t("vergleich.scriptNichtImSnapshot")}
            </div>
          </div>;
        }
        return <div style={{marginTop:8}}>
          <div style={{fontSize:9,fontWeight:600,color:"var(--text-3)",textTransform:"uppercase",letterSpacing:.3,marginBottom:4}}>
            {t("gemeinsam:bauteil.vergleich.scriptMitName",{name:resolvedName})}
          </div>
          <pre style={{margin:0,padding:"6px 8px",background:isDark?"rgba(0,0,0,0.3)":"rgba(0,0,0,0.05)",borderRadius:4,fontSize:9,fontFamily:MONO,color:isDark?"#a5f3fc":"#0f766e",maxHeight:180,overflow:"auto",whiteSpace:"pre-wrap",wordBreak:"break-all"}}>
            {content.length>800?content.substring(0,800)+"\n[...]":content}
          </pre>
        </div>;
      })()}
    </div>
  </div>;
}


// Diff-Pane: Karten-Kopf (Vorher/Nachher) + zentrale IflowDiagram-Ansicht + Element-Popup.
// Die komplette Diff-Bildsprache (Rahmen, Ecken-Badge, NEU/WEG/GEAEND., Dimmen) liefert
// IflowDiagram ueber die Props marks/onlyChanges; hier wird nur das Detail-Popup verdrahtet.
function DiffPane({xml,label,side,tone,marks,onlyChanges,inspected,setInspected,parsedBefore,parsedAfter,diffIds,scripts}){
  const isMobile=typeof window!=="undefined"&&window.innerWidth<768;
  const open=inspected&&inspected.side===side?inspected.id:null;
  return <div style={{flex:1,minWidth:0,display:"flex",flexDirection:"column",minHeight:isMobile?300:undefined}}>
    <div style={{fontSize:10,fontWeight:600,color:"var(--iio-text-2)",padding:"5px 9px",background:"var(--iio-bg-2)",
      border:"1px solid var(--iio-border)",borderBottom:"none",borderRadius:"6px 6px 0 0",display:"flex",alignItems:"center",gap:6}}>
      <span style={{width:8,height:8,borderRadius:2,background:tone,flexShrink:0}}/>
      {label}
    </div>
    <div style={{flex:1,minHeight:isMobile?300:0,position:"relative",overflow:"hidden",
      border:"1px solid var(--iio-border)",borderRadius:"0 0 6px 6px"}}>
      <IflowDiagram bpmnXml={xml} marks={marks} onlyChanges={onlyChanges} height="100%"
        selectedId={open||undefined} onElementClick={el=>setInspected({id:el.id,side})}/>
      {open&&<ElementInspectPopup element={{id:open}} side={side}
        parsedBefore={parsedBefore} parsedAfter={parsedAfter} diffIds={diffIds}
        onClose={()=>setInspected(null)} scripts={scripts}/>}
    </div>
  </div>;
}

// Main BPMN Diff Viewer component
function BpmnDiffViewer({entryId,meta,diffCache,setDiffCache}){
  const{t}=useTranslation(["auditlog","gemeinsam"]);
  const[viewMode,setViewMode]=useState("bpmn"); // "bpmn" | "files" | "srcxml" | "tgtxml"
  const[bpmnData,setBpmnData]=useState(null);
  const[bpmnLoading,setBpmnLoading]=useState(true);
  const[bpmnError,setBpmnError]=useState(null);
  const[selectedArtId,setSelectedArtId]=useState(null);
  const[onlyChanges,setOnlyChanges]=useState(false);
  const[inspected,setInspected]=useState(null); // {id, side:"before"|"after"} | null
  const isPackage=meta?.type==="package";
  const artifactIds=meta?.artifactIds||[];

  // Load BPMN (before/after)
  useEffect(()=>{
    setBpmnLoading(true);setBpmnError(null);setBpmnData(null);
    const artParam=selectedArtId||artifactIds[0];
    const url=isPackage&&artParam
      ?`/api/audit-log/${entryId}/bpmn?artifactId=${encodeURIComponent(artParam)}`
      :`/api/audit-log/${entryId}/bpmn`;
    api(url).then(d=>{setBpmnData(d);setBpmnLoading(false);})
      .catch(e=>{setBpmnError(e.message||t("gemeinsam:zustand.fehler"));setBpmnLoading(false);});
  },[entryId,selectedArtId]);

  const srcBpmn=bpmnData?.before?.bpmn;
  const tgtBpmn=bpmnData?.after?.bpmn;
  const srcVersion=bpmnData?.before?.version||bpmnData?.before?.artifactVersion||bpmnData?.before?.meta?.version||null;
  const tgtVersion=bpmnData?.after?.version||bpmnData?.after?.artifactVersion||bpmnData?.after?.meta?.version||null;
  const srcName=(bpmnData?.before?.instanceName||t("gemeinsam:bauteil.vergleich.zielsystem"))+" - iFlow"+(srcVersion?" v"+srcVersion:"");
  const tgtName=(bpmnData?.after?.instanceName||t("gemeinsam:bauteil.vergleich.zielsystem"))+" - iFlow"+(tgtVersion?" v"+tgtVersion:"");
  const parsedBefore=srcBpmn?parseBpmnElements(srcBpmn):{};
  const parsedAfter=tgtBpmn?parseBpmnElements(tgtBpmn):{};
  const diffIds=srcBpmn&&tgtBpmn?diffBpmnElements(parsedBefore,parsedAfter):null;
  const bpmnScripts=bpmnData?.scripts||{};
  // marks-Zuordnung fuer IflowDiagram (Shapes UND Kanten, da nach Element-/Kanten-ID):
  // ALTE Version -> WEG (del, entfernte IDs) + GEAEND. (chg); NEUE Version -> NEU (add) + GEAEND. (chg).
  const marksBefore={},marksAfter={};
  if(diffIds){
    diffIds.removed.forEach(id=>{marksBefore[id]="del";});
    diffIds.changed.forEach(id=>{marksBefore[id]="chg";marksAfter[id]="chg";});
    diffIds.added.forEach(id=>{marksAfter[id]="add";});
  }
  const hasChanges=diffIds&&(diffIds.added.length+diffIds.removed.length+diffIds.changed.length>0);
  const totalChanges=diffIds?(diffIds.added.length+diffIds.removed.length+diffIds.changed.length):0;

  const tabBtn=(id,label,badge)=><button onClick={()=>setViewMode(id)} style={{
    padding:"4px 12px",borderRadius:6,fontSize:10,fontWeight:viewMode===id?600:400,cursor:"pointer",fontFamily:FONT,
    border:`1px solid ${viewMode===id?"var(--accent)":"var(--border)"}`,
    background:viewMode===id?"var(--accent-glow)":"var(--surface-0)",color:viewMode===id?"var(--accent)":"var(--text-2)",
    display:"flex",alignItems:"center",gap:4
  }}>{label}{badge&&<span style={{background:"var(--accent)",color:"#fff",borderRadius:4,padding:"0 5px",fontSize:10,fontWeight:700,lineHeight:"14px"}}>{badge}</span>}</button>;

  // Artifact selector for package transports
  const ArtSelector=isPackage&&artifactIds.length>1?<div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
    <span style={{fontSize:10,color:"var(--text-3)"}}>{t("vergleich.artefakt")}</span>
    <select value={selectedArtId||artifactIds[0]}
      onChange={e=>{setSelectedArtId(e.target.value);setBpmnData(null);setInspected(null);}}
      style={{fontSize:10,background:"var(--surface-1)",color:"var(--text-1)",border:"1px solid var(--border)",borderRadius:4,padding:"3px 8px",fontFamily:FONT}}>
      {artifactIds.map(id=><option key={id} value={id}>{id}</option>)}
    </select>
  </div>:null;

  const renderXmlPanel=(xml,label)=><div>
    <div style={{fontSize:10,fontWeight:600,color:"var(--text-3)",marginBottom:6,textTransform:"uppercase",letterSpacing:.3}}>{label}</div>
    <pre style={{fontFamily:MONO,fontSize:10,lineHeight:1.6,color:"var(--text-2)",whiteSpace:"pre-wrap",wordBreak:"break-all",
      maxHeight:500,overflow:"auto",background:"var(--surface-0)",padding:10,borderRadius:6,border:"1px solid var(--border-subtle)"}}>{xml}</pre>
  </div>;

  return <div style={{marginTop:8}}>
    {ArtSelector}
    {/* Tab bar */}
    <div style={{display:"flex",gap:4,marginBottom:10,flexWrap:"wrap"}}>
      {tabBtn("bpmn",t("vergleich.reiterIflow"),hasChanges?totalChanges:null)}
      {tabBtn("files",t("vergleich.reiterDateien"))}
      {srcBpmn&&tabBtn("srcxml",t("vergleich.reiterVorherXml"))}
      {tgtBpmn&&tabBtn("tgtxml",t("vergleich.reiterNachherXml"))}
    </div>

    {/* BPMN visual side-by-side */}
    {viewMode==="bpmn"&&<div>
      {bpmnLoading&&<SkeletonLoader/>}
      {bpmnError&&<div style={{padding:12,color:"var(--danger)",fontSize:11}}>{t("vergleich.ladefehler",{meldung:bpmnError})}</div>}
      {!bpmnLoading&&!bpmnError&&!bpmnData?.available&&<div style={{padding:16,color:"var(--text-3)",fontSize:11,background:"var(--surface-0)",borderRadius:8,border:"1px solid var(--border-subtle)",textAlign:"center"}}>
        <div style={{fontSize:13,marginBottom:4}}>{t("vergleich.keinSnapshotTitel")}</div>
        <div style={{fontSize:11}}>{t("vergleich.keinSnapshotText")}</div>
      </div>}
      {!bpmnLoading&&bpmnData?.available&&<div>
        {/* Steuerleiste: "Nur Änderungen"-Filter + kompakte Legende (Badge-Bildsprache wie IflowDiagram) */}
        {hasChanges&&<div style={{display:"flex",alignItems:"center",gap:16,flexWrap:"wrap",marginBottom:8}}>
          <label style={{display:"inline-flex",alignItems:"center",gap:7,cursor:"pointer",fontSize:11,color:"var(--iio-text-2)",userSelect:"none"}}>
            <input type="checkbox" checked={onlyChanges} onChange={e=>setOnlyChanges(e.target.checked)}
              style={{accentColor:"var(--iio-signal)",cursor:"pointer",width:14,height:14}}/>
            {t("gemeinsam:bauteil.vergleich.nurAenderungen")}
          </label>
          <div style={{display:"flex",gap:14,fontSize:11,color:"var(--iio-text-2)",flexWrap:"wrap"}}>
            {/* Schluessel statt Text als React-key: die Legende darf beim Sprachwechsel nicht neu aufgebaut werden. */}
            {[["+","gemeinsam:bauteil.diff.neu","var(--iio-dia-diff-add)"],["−","gemeinsam:bauteil.diff.weg","var(--iio-dia-diff-del)"],["Δ","gemeinsam:bauteil.diff.geaendert","var(--iio-dia-diff-chg)"]].map(([badge,schluessel,col])=>
              <span key={schluessel} style={{display:"inline-flex",alignItems:"center",gap:6}}>
                <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:16,height:16,color:col,fontSize:11,fontWeight:700,lineHeight:1}}>{badge}</span>
                {t(schluessel)}
              </span>
            )}
          </div>
        </div>}
        {!hasChanges&&srcBpmn&&tgtBpmn&&<div style={{padding:"8px 12px",borderRadius:4,background:`${v("success")}14`,border:`1px solid ${v("success")}30`,fontSize:11,color:v("success"),marginBottom:8}}>
          {t("vergleich.keineStrukturaenderung",{reiter:t("vergleich.reiterDateien")})}
        </div>}
        {srcVersion&&tgtVersion&&<div style={{display:"flex",justifyContent:"center",marginBottom:6}}>
          <span style={{fontSize:11,fontWeight:600,fontFamily:MONO,color:"var(--text-3)"}}>v{srcVersion}</span>
          <span style={{fontSize:11,margin:"0 6px",color:"var(--text-3)"}}>{"\u2192"}</span>
          <span style={{fontSize:11,fontWeight:600,fontFamily:MONO,color:"var(--iio-success)"}}>v{tgtVersion}</span>
        </div>}
        {srcBpmn||tgtBpmn?<div style={{display:"flex",flexDirection:typeof window!=="undefined"&&window.innerWidth<768?"column":"row",gap:8,height:typeof window!=="undefined"&&window.innerWidth<768?"auto":440}}>
          {srcBpmn
            ?<DiffPane xml={srcBpmn} label={t("gemeinsam:bauteil.vergleich.paneVorher",{system:srcName,version:""})} side="before" tone="var(--iio-critical)"
              marks={marksBefore} onlyChanges={onlyChanges} inspected={inspected} setInspected={setInspected}
              parsedBefore={parsedBefore} parsedAfter={parsedAfter} diffIds={diffIds} scripts={bpmnScripts}/>
            :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--surface-0)",border:"1px solid var(--border)",borderRadius:6,fontSize:11,color:"var(--text-3)"}}>{t("vergleich.keinVorherSnapshot")}</div>}
          {tgtBpmn
            ?<DiffPane xml={tgtBpmn} label={t("gemeinsam:bauteil.vergleich.paneNachher",{system:tgtName,version:""})} side="after" tone="var(--iio-success)"
              marks={marksAfter} onlyChanges={onlyChanges} inspected={inspected} setInspected={setInspected}
              parsedBefore={parsedBefore} parsedAfter={parsedAfter} diffIds={diffIds} scripts={bpmnScripts}/>
            :<div style={{flex:1,display:"flex",alignItems:"center",justifyContent:"center",background:"var(--surface-0)",border:"1px solid var(--border)",borderRadius:6,fontSize:11,color:"var(--text-3)"}}>{t("vergleich.keinNachherSnapshot")}</div>}
        </div>:<div style={{padding:16,color:"var(--text-3)",fontSize:11}}>{t("vergleich.keinXml")}</div>}
      </div>}
    </div>}

    {/* Code diff tab */}
    {viewMode==="files"&&<ChangedFilesViewer entryId={entryId} meta={meta} diffCache={diffCache} setDiffCache={setDiffCache} selectedArtId={selectedArtId||artifactIds[0]}/>}

    {/* Raw XML tabs */}
    {viewMode==="srcxml"&&srcBpmn&&renderXmlPanel(srcBpmn,t("vergleich.xmlVorherTitel",{name:srcName}))}
    {viewMode==="tgtxml"&&tgtBpmn&&renderXmlPanel(tgtBpmn,t("vergleich.xmlNachherTitel",{name:tgtName}))}
  </div>;
}

/* ============================================================================
 * "Das Bordbuch" - Audit Log im Integration-in-One-Design.
 * Drei Zonen: Seismogramm (Tages-Navigation) | Erzählstrang (Tageskapitel) |
 * Marginalie (Notizen). Detail als Dossier-Overlay mit BpmnDiffViewer.
 * Die Diff-/BPMN-Komponenten oberhalb bleiben unverändert.
 * ==========================================================================*/

// Die Kennung links ist der Wert aus der Datenhaltung und geht so an den Server
// (?category=...). Uebersetzt wird nur die Anzeige rechts, ueber den Katalog.
const ADT_CATS=[
  {id:"all",labelKey:"kategorie.alle"},
  {id:"transport",labelKey:"kategorie.transport"},
  {id:"transport_request",labelKey:"kategorie.transportauftrag"},
  {id:"config",labelKey:"kategorie.konfiguration"},
  {id:"alerting",labelKey:"kategorie.alerting"},
  {id:"user",labelKey:"kategorie.benutzerRechte"},
];
const ADT_CAT_KEY={transport:"kategorie.transport",transport_request:"kategorie.transportauftrag",config:"kategorie.konfiguration",alerting:"kategorie.alerting",user:"kategorie.benutzerRechte"};

function adtStatusKey(status){
  if(status==="ok"||status==="success")return"ok";
  if(status==="warning"||status==="warn")return"warn";
  if(status==="pending"||status==="running")return"idle";
  return"crit";
}
const ADT_STATUS_KEY={ok:"status.ok",warn:"status.warn",crit:"status.crit",idle:"status.idle"};
const adtActionLabel=(entry,t)=>entry.action==="cicd_transport"?t("vorgang.cicdTransport"):entry.action;

function AdtCatIcon({cat}){
  const p={width:14,height:14,viewBox:"0 0 24 24",fill:"none",stroke:"currentColor",strokeWidth:1.8,strokeLinecap:"round",strokeLinejoin:"round"};
  if(cat==="transport")return<svg {...p}><path d="m16 3 4 4-4 4"/><path d="M20 7H4"/><path d="m8 21-4-4 4-4"/><path d="M4 17h16"/></svg>;
  if(cat==="transport_request")return<svg {...p}><path d="M7 4v16"/><path d="M17 4v16"/><path d="m9.5 9 2.5 3-2.5 3"/></svg>;
  if(cat==="config")return<svg {...p}><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>;
  if(cat==="alerting")return<svg {...p}><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;
  if(cat==="user")return<svg {...p}><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
  return<svg {...p}><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>;
}

function adtDayKey(ts){
  const d=new Date(ts);
  return d.getFullYear()+"-"+String(d.getMonth()+1).padStart(2,"0")+"-"+String(d.getDate()).padStart(2,"0");
}
// Monats- und Wochentagsnamen kommen aus dem Gebietsschema, nicht aus einer
// festen Liste: nur so steht im Englischen "12 August" statt "12. August".
function adtFmtDay(key,t,gebiet){
  const d=new Date(key+"T12:00:00");
  const now=new Date();
  const today=adtDayKey(now.toISOString());
  const yest=adtDayKey(new Date(now.getTime()-864e5).toISOString());
  const datum=d.toLocaleDateString(gebiet,{day:"numeric",month:"long",year:"numeric"});
  if(key===today)return t("chronik.heute",{datum});
  if(key===yest)return t("chronik.gestern",{datum});
  return t("chronik.wochentag",{tag:d.toLocaleDateString(gebiet,{weekday:"long"}),datum});
}
function adtFmtDayShort(key,gebiet){
  const d=new Date(key+"T12:00:00");
  return d.toLocaleDateString(gebiet,{day:"numeric",month:"long"});
}

const AuditLog=()=>{
  const{t,i18n}=useTranslation(["auditlog","gemeinsam"]);
  // Ein Protokoll lebt von eindeutigen Zeitangaben: "12.08.2026" liest sich im
  // Englischen als 8. Dezember, deshalb folgt das Gebietsschema der Sprache.
  const gebiet=i18n.language==="en"?"en-GB":"de-DE";
  const{toasts,addToast}=useToast();
  const[logs,setLogs]=useState([]);
  const[allLogs,setAllLogs]=useState([]);
  const[loading,setLoading]=useState(true);
  const[filter,setFilter]=useState("all");
  const[search,setSearch]=useState("");
  const[modalEntry,setModalEntry]=useState(null);
  const[diffCache,setDiffCache]=useState({});
  const[dateMode,setDateMode]=useState("all");
  const[customFrom,setCustomFrom]=useState("");
  const[customTo,setCustomTo]=useState("");
  const[noteEditId,setNoteEditId]=useState(null);
  const[noteText,setNoteText]=useState("");
  const[noteSaving,setNoteSaving]=useState(false);
  const[noteConfirmId,setNoteConfirmId]=useState(null);
  const[freshNoteId,setFreshNoteId]=useState(null);
  const firstLoadRef=useRef(true);
  const pageRef=useRef(null);
  const filterbarRef=useRef(null);
  const catsRef=useRef(null);
  const inkRef=useRef(null);
  const chronicleRef=useRef(null);
  const seismoFrameRef=useRef(null);
  const seismoTrackRef=useRef(null);

  useEffect(()=>{
    setLoading(true);
    api(`/api/audit-log?category=${filter}&limit=500`).then(d=>{
      setLogs(d);
      if(filter==="all")setAllLogs(d);
      setLoading(false);
      firstLoadRef.current=false;
    }).catch(()=>setLoading(false));
  },[filter]);


  // ESC: erst offene Notiz-Interaktion schließen, dann das Dossier
  const escRef=useRef({});
  escRef.current={noteEditId,noteConfirmId,modalEntry};
  useEffect(()=>{
    const h=(e)=>{
      if(e.key!=="Escape")return;
      const s=escRef.current;
      if(s.noteConfirmId)setNoteConfirmId(null);
      else if(s.noteEditId)setNoteEditId(null);
      else if(s.modalEntry)setModalEntry(null);
    };
    window.addEventListener("keydown",h);
    return()=>window.removeEventListener("keydown",h);
  },[]);

  // Body-Scroll sperren solange das Dossier offen ist
  useEffect(()=>{
    document.body.style.overflow=modalEntry?"hidden":"";
    return()=>{document.body.style.overflow="";};
  },[modalEntry]);

  const openModal=(entry)=>{setNoteEditId(null);setNoteConfirmId(null);setModalEntry(entry);};
  const applyReportAuditStatus=useCallback((id,status)=>{
    const update=entries=>{
      if(!entries.some(entry=>entry.id===id&&entry.status!==status))return entries;
      return entries.map(entry=>entry.id===id?{...entry,status}:entry);
    };
    setLogs(update);
    setAllLogs(update);
    setModalEntry(entry=>entry?.id===id&&entry.status!==status?{...entry,status}:entry);
  },[]);

  // ── Notizen (Marginalie + Dossier teilen sich die Handler) ──
  const startNote=(entry,e)=>{
    if(e)e.stopPropagation();
    setNoteConfirmId(null);
    setNoteEditId(entry.id);
    setNoteText(entry.note||"");
  };
  const applyNoteLocal=(id,note)=>{
    setLogs(prev=>prev.map(l=>l.id===id?{...l,note}:l));
    setAllLogs(prev=>prev.map(l=>l.id===id?{...l,note}:l));
    setModalEntry(prev=>prev&&prev.id===id?{...prev,note}:prev);
  };
  const saveNote=async(id)=>{
    setNoteSaving(true);
    try{
      await api(`/api/audit-log/${id}/note`,{method:"PUT",body:JSON.stringify({note:noteText})});
      applyNoteLocal(id,noteText.trim()||null);
      addToast(t("notiz.gespeichert"),"success");
      setNoteEditId(null);
      setFreshNoteId(id);
      setTimeout(()=>setFreshNoteId(null),700);
    }catch(e){addToast(t("notiz.speichernFehler"),"error");}
    setNoteSaving(false);
  };
  const deleteNote=async(id)=>{
    setNoteSaving(true);
    try{
      await api(`/api/audit-log/${id}/note`,{method:"PUT",body:JSON.stringify({note:""})});
      applyNoteLocal(id,null);
      addToast(t("notiz.geloescht"),"success");
      setNoteConfirmId(null);setNoteEditId(null);
    }catch(e){addToast(t("notiz.loeschenFehler"),"error");}
    setNoteSaving(false);
  };

  // ── Filter-Logik (identisch zur Vorgängerversion) ──
  const inDateRange=useCallback((l)=>{
    if(dateMode==="all")return true;
    const t=new Date(l.timestamp).getTime();
    if(dateMode==="custom"){
      const from=customFrom?new Date(customFrom).getTime():0;
      const to=customTo?new Date(customTo+"T23:59:59").getTime():Infinity;
      return t>=from&&t<=to;
    }
    return t>=Date.now()-parseInt(dateMode)*24*60*60*1000;
  },[dateMode,customFrom,customTo]);

  const matchesSearch=useCallback((l)=>{
    if(!search)return true;
    const s=search.toLowerCase();
    return (adtActionLabel(l,t)||"").toLowerCase().includes(s)||(l.action||"").toLowerCase().includes(s)||(l.userName||"").toLowerCase().includes(s)||(l.instanceName||"").toLowerCase().includes(s)||(l.spaceName||"").toLowerCase().includes(s);
  },[search,t]);

  const dateFiltered=useMemo(()=>logs.filter(inDateRange),[logs,inDateRange]);
  const filtered=useMemo(()=>dateFiltered.filter(matchesSearch),[dateFiltered,matchesSearch]);

  // Kategorie-Zähler im Register: Basis ist der letzte "Alle"-Abruf,
  // Zeitraum + Suche fließen ein, die gewählte Kategorie nicht.
  const catCounts=useMemo(()=>{
    const c={all:0,transport:0,transport_request:0,config:0,alerting:0,user:0};
    allLogs.forEach(l=>{
      if(!inDateRange(l)||!matchesSearch(l))return;
      c.all++;
      if(c[l.category]!==undefined)c[l.category]++;
    });
    return c;
  },[allLogs,inDateRange,matchesSearch]);

  // Tageskapitel in Server-Reihenfolge (neueste zuerst)
  const chapters=useMemo(()=>{
    const map=new Map();
    filtered.forEach(l=>{
      const k=adtDayKey(l.timestamp);
      if(!map.has(k))map.set(k,[]);
      map.get(k).push(l);
    });
    return Array.from(map.entries());
  },[filtered]);

  const seismoDays=useMemo(()=>chapters.map(([day,entries])=>({
    day,
    n:entries.length,
    fails:entries.filter(l=>adtStatusKey(l.status)==="crit").length,
    warns:entries.filter(l=>adtStatusKey(l.status)==="warn").length,
  })),[chapters]);
  const maxDayN=Math.max(1,...seismoDays.map(d=>d.n),1);

  // Filterleisten-Höhe + Register-Marke nachführen
  useEffect(()=>{
    const measure=()=>{
      if(pageRef.current&&filterbarRef.current)
        pageRef.current.style.setProperty("--adt-fbh",filterbarRef.current.offsetHeight+"px");
      if(catsRef.current&&inkRef.current){
        const b=catsRef.current.querySelector("button.on");
        if(b){inkRef.current.style.left=b.offsetLeft+"px";inkRef.current.style.width=b.offsetWidth+"px";}
      }
    };
    measure();
    const t=setTimeout(measure,300);
    window.addEventListener("resize",measure);
    return()=>{window.removeEventListener("resize",measure);clearTimeout(t);};
  },[filter,dateMode,loading,catCounts]);

  // Seismogramm: Viewport-Rahmen folgt dem Scroll
  useEffect(()=>{
    const onScroll=()=>{
      const fr=seismoFrameRef.current,tr=seismoTrackRef.current,ch=chronicleRef.current;
      if(!fr||!tr||!ch)return;
      const rect=ch.getBoundingClientRect();
      const total=rect.height;
      if(total<=0)return;
      const visTop=Math.min(Math.max(0,-rect.top+140),total);
      const visH=Math.max(60,Math.min(window.innerHeight-200,total-visTop));
      const trackH=tr.clientHeight;
      const fh=Math.max(24,Math.min(trackH,trackH*(visH/total)));
      const fy=Math.min(trackH*(visTop/total),Math.max(0,trackH-fh));
      fr.style.height=fh+"px";
      fr.style.transform=`translateY(${fy}px)`;
    };
    let raf=null;
    const handler=()=>{if(raf)return;raf=requestAnimationFrame(()=>{raf=null;onScroll();});};
    window.addEventListener("scroll",handler,{passive:true});
    window.addEventListener("resize",handler);
    onScroll();
    return()=>{window.removeEventListener("scroll",handler);window.removeEventListener("resize",handler);if(raf)cancelAnimationFrame(raf);};
  },[chapters,loading]);

  const exportCsv=async()=>{
    try{
      const res=await fetch("/api/audit-log/export",{headers:{"Authorization":"Bearer "+getToken()}});
      if(!res.ok){const e=await res.json().catch(()=>({}));addToast(e.error||t("export.fehler"),"error");return;}
      const blob=await res.blob();
      const url=URL.createObjectURL(blob);
      const a=document.createElement("a");
      a.href=url;a.download="audit-log.csv";a.click();
      URL.revokeObjectURL(url);
      addToast(t("export.erfolg"),"success");
    }catch(e){addToast(t("export.fehler"),"error");}
  };

  const formatTime=(ts)=>{
    if(!ts)return "-";
    const d=new Date(ts);
    return d.toLocaleDateString(gebiet,{day:"2-digit",month:"2-digit",year:"2-digit"})+" "+d.toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit"});
  };
  const formatClock=(ts)=>{
    if(!ts)return "-";
    return new Date(ts).toLocaleTimeString(gebiet,{hour:"2-digit",minute:"2-digit"});
  };

  const isTransportWithBpmn=(l)=>l.category==="transport"&&(l.meta?.flowId||l.meta?.hasSnapshot||(l.meta?.type==="package"&&l.meta?.artifactIds?.length));

  const resetFilters=()=>{setSearch("");setFilter("all");setDateMode("all");};
  const hasActiveFilter=!!search||filter!=="all"||dateMode!=="all";

  const m=modalEntry;
  const mSk=m?adtStatusKey(m.status):null;

  // ── Notiz-Bausteine (Marginalie) ──
  const noteEditor=(l)=>(
    <div className="iio-adt-note-editor" onClick={e=>e.stopPropagation()}>
      <textarea autoFocus value={noteText} onChange={e=>setNoteText(e.target.value)}
        placeholder={t("notiz.platzhalter")}
        onKeyDown={e=>{if((e.ctrlKey||e.metaKey)&&e.key==="Enter")saveNote(l.id);}}/>
      <div className="iio-adt-note-row">
        <button className="iio-btn iio-btn--primary iio-btn--sm" disabled={noteSaving} onClick={()=>saveNote(l.id)}>{noteSaving?t("notiz.speichernLaeuft"):t("notiz.anDenRand")}</button>
        <button className="iio-btn iio-btn--tertiary iio-btn--sm" onClick={()=>setNoteEditId(null)}>{t("gemeinsam:aktion.abbrechen")}</button>
      </div>
    </div>
  );
  const noteConfirm=(l)=>(
    <div className="iio-adt-note-confirm" onClick={e=>e.stopPropagation()}>
      <span>{t("notiz.entfernenFrage")}</span>
      <div className="iio-adt-note-row">
        <button className="iio-btn iio-btn--danger iio-btn--sm" disabled={noteSaving} onClick={()=>deleteNote(l.id)}>{t("notiz.entfernen")}</button>
        <button className="iio-btn iio-btn--tertiary iio-btn--sm" onClick={()=>setNoteConfirmId(null)}>{t("notiz.behalten")}</button>
      </div>
    </div>
  );
  const marginCell=(l)=>(
    <div className="iio-adt-margin" onClick={e=>e.stopPropagation()}>
      {noteConfirmId===l.id?noteConfirm(l)
      :noteEditId===l.id?noteEditor(l)
      :l.note?(
        <div className={"iio-adt-note"+(freshNoteId===l.id?" is-fresh":"")}>
          <span className="iio-adt-note-rule"/>
          <div className="iio-adt-note-text">{l.note}</div>
          <div className="iio-adt-note-actions">
            <button onClick={e=>startNote(l,e)}>{t("gemeinsam:aktion.bearbeiten")}</button>
            <button className="is-danger" onClick={e=>{e.stopPropagation();setNoteEditId(null);setNoteConfirmId(l.id);}}>{t("notiz.entfernen")}</button>
          </div>
        </div>
      ):(
        <button className="iio-adt-note-ghost" onClick={e=>startNote(l,e)} title={t("notiz.ghostTitel")}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/></svg>
          {t("notiz.ghost")}
        </button>
      )}
    </div>
  );

  return <div className="iio-adt" ref={pageRef}>
    <ToastContainer toasts={toasts}/>

    {/* ── Page Head (Editorial-Pattern) ── */}
    <div className="iio-page-head">
      <div className="iio-page-head-left">
        <div className="iio-breadcrumb">
          <span className="iio-accent">{t("kopf.brotkrume")}</span>
          <span className="iio-sep">/</span>
          <span>{t("kopf.brotkrumeAktuell")}</span>
        </div>
        <h1>{t("kopf.titel")}</h1>
        <p className="iio-lead">
          {t("kopf.einleitung")}
        </p>
      </div>
      <div className="iio-page-head-right">
        <button className="iio-btn iio-btn--signal" onClick={exportCsv} title={t("kopf.exportTitel")}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
          {t("kopf.export")}
        </button>
      </div>
    </div>

    {/* ── Filterleiste: Kapitelregister + Instrumentenzeile ── */}
    <div className="iio-adt-filterbar" ref={filterbarRef}>
      <div className="iio-adt-cats" ref={catsRef}>
        {ADT_CATS.map(c=>(
          <button key={c.id} className={filter===c.id?"on":""} onClick={()=>setFilter(c.id)} type="button">
            <AdtCatIcon cat={c.id}/>
            <span className="cl">{t(c.labelKey)}</span>
            <span className="cn">{catCounts[c.id]}</span>
          </button>
        ))}
        <span className="iio-adt-cat-ink" ref={inkRef}/>
      </div>
      <div className="iio-adt-row2">
        <span className="iio-adt-microcaps">{t("zeitraum.titel")}</span>
        <div className="iio-adt-timeseg">
          {["all","7","30","90","custom"].map(md=>(
            <button key={md} type="button" className={dateMode===md?"on":""} onClick={()=>setDateMode(md)}>
              {md==="all"?t("zeitraum.gesamt"):md==="custom"?t("zeitraum.eigener"):t("zeitraum.tage",{count:Number(md)})}
            </button>
          ))}
        </div>
        {dateMode==="custom"&&(
          <div className="iio-adt-custom">
            <label>{t("zeitraum.von")}</label>
            <input type="date" value={customFrom} onChange={e=>setCustomFrom(e.target.value)}/>
            <label>{t("zeitraum.bis")}</label>
            <input type="date" value={customTo} onChange={e=>setCustomTo(e.target.value)}/>
          </div>
        )}
        <div className="iio-adt-fbright">
          <span className="iio-adt-count">
            {hasActiveFilter?t("filter.vonGesamt",{n:filtered.length,gesamt:logs.length}):t("chronik.eintraege",{count:filtered.length})}
          </span>
          {hasActiveFilter&&<button className="iio-adt-reset" type="button" onClick={resetFilters}>{t("filter.zuruecksetzen")}</button>}
          <div className={"iio-adt-search"+(search?" has-q":"")}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/></svg>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder={t("filter.suchePlatzhalter")}/>
            {search&&<button className="clr" type="button" onClick={()=>setSearch("")} aria-label={t("filter.sucheLeeren")}>&times;</button>}
          </div>
        </div>
      </div>
    </div>

    {/* ── Drei Zonen: Seismogramm | Erzählstrang (+ Marginalie pro Zeile) ── */}
    <div className="iio-adt-book">
      <aside className="iio-adt-seismo">
        <div className="iio-adt-seismo-track" ref={seismoTrackRef}>
          {seismoDays.map(sd=>{
            const w=Math.round(4+36*Math.log(sd.n+1)/Math.log(maxDayN+1));
            const tip=adtFmtDayShort(sd.day,gebiet)+" · "+t("chronik.eintraege",{count:sd.n})+(sd.fails?" · "+t("chronik.fehler",{count:sd.fails}):sd.warns?" · "+t("chronik.warnungen",{count:sd.warns}):"");
            return <button key={sd.day} type="button" className="iio-adt-seismo-day" title={tip}
              onClick={()=>{const el=document.getElementById("adt-day-"+sd.day);if(el)el.scrollIntoView({behavior:"smooth",block:"start"});}}>
              <span className="bar" style={{width:w}}>
                {(sd.fails>0||sd.warns>0)&&<span className={"tip "+(sd.fails>0?"is-crit":"is-warn")}/>}
              </span>
            </button>;
          })}
        </div>
        <div className="iio-adt-seismo-frame" ref={seismoFrameRef}/>
      </aside>

      <div className="iio-adt-chronicle" ref={chronicleRef}
        key={filter+"|"+dateMode+"|"+(loading?"l":"r")}>

        {loading&&<div className="iio-adt-chapter">
          <div className="iio-adt-chap-head"><span className="d">{t("chronik.laedt")}</span><span className="line"/></div>
          {[0,1,2].map(i=>(
            <div className="iio-adt-row" key={i}>
              <div className="iio-adt-entry is-skel">
                <span className="sk" style={{width:60,animationDelay:(i*0.15)+"s"}}/>
                <span className="sk" style={{width:"46%",animationDelay:(i*0.15+0.05)+"s"}}/>
                <span className="sk" style={{width:"20%",animationDelay:(i*0.15+0.1)+"s"}}/>
              </div>
              <div className="iio-adt-margin"/>
            </div>
          ))}
        </div>}

        {!loading&&chapters.length===0&&(
          <div className="iio-adt-empty">
            <svg width="150" height="92" viewBox="0 0 150 92" fill="none" stroke="var(--iio-text-4)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M75 18 C62 9 44 7 26 11 V72 C44 68 62 70 75 80 C88 70 106 68 124 72 V11 C106 7 88 9 75 18 Z"/>
              <path d="M75 18 V80"/>
              <path d="M36 26 h26 M36 36 h26 M36 46 h18" strokeWidth="1.3" opacity=".6"/>
              <path d="M118 30 c-9 1 -16 8 -22 18 l-4 10 l10 -4 c10 -6 17 -13 18 -22 z" stroke="var(--iio-signal)"/>
              <path d="M92 58 l-4 6" stroke="var(--iio-signal)"/>
            </svg>
            {hasActiveFilter?<>
              <h3>{t("leer.gefiltertTitel")}</h3>
              <p>{search?t("leer.gefiltertSuche",{suche:search}):t("leer.gefiltertZeitraum")}</p>
              <button className="iio-btn iio-btn--secondary" onClick={resetFilters}>{t("filter.zuruecksetzen")}</button>
            </>:<>
              <h3>{t("leer.titel")}</h3>
              <p>{t("leer.text")}</p>
            </>}
          </div>
        )}

        {!loading&&chapters.map(([day,entries],ci)=>{
          const fails=entries.filter(l=>adtStatusKey(l.status)==="crit").length;
          let baseIdx=0;
          for(let i=0;i<ci;i++)baseIdx+=chapters[i][1].length;
          return <section className="iio-adt-chapter" key={day} id={"adt-day-"+day}>
            <div className="iio-adt-chap-head">
              <span className="d">{adtFmtDay(day,t,gebiet)}</span>
              <span className="line"/>
              <span className="s">{t("chronik.eintraege",{count:entries.length})}{fails>0&&<> · <em>{t("chronik.fehler",{count:fails})}</em></>}</span>
            </div>
            {entries.map((l,i)=>{
              const sk=adtStatusKey(l.status);
              const idx=baseIdx+i;
              const animate=idx<14;
              const hasBpmn=isTransportWithBpmn(l)||l.meta?.hasSnapshot;
              return <div className="iio-adt-row" key={l.id||(day+"-"+i)}>
                <div className={"iio-adt-entry"+(animate?" anim":"")}
                  style={animate?{animationDelay:(idx*30)+"ms"}:undefined}
                  onClick={()=>openModal(l)}>
                  <div className="iio-adt-time">
                    <span className="t">{formatClock(l.timestamp)}</span>
                    <span className="u">{l.userName||"-"}</span>
                  </div>
                  <span className={"iio-adt-bar is-"+sk}/>
                  <div className="iio-adt-main">
                    {/* Kategorie: bekannte Kennung uebersetzt, unbekannte bleibt woertlich stehen. */}
                    <div className="cat">{ADT_CAT_KEY[l.category]?t(ADT_CAT_KEY[l.category]):(l.category||"-")}</div>
                    {/* Freitext bleibt unverändert; maschinenlesbare Vorgänge erhalten eine Beschriftung. */}
                    <div className="act">{adtActionLabel(l,t)}</div>
                    <div className="sub">{[l.instanceName,l.spaceName].filter(Boolean).join(" · ")||"-"}</div>
                    {l.note&&<div className="note-inline">{l.note}</div>}
                  </div>
                  {hasBpmn&&<span className="iio-adt-cmp">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="6" width="8" height="12" rx="1.5"/><rect x="14" y="6" width="8" height="12" rx="1.5"/><path d="M10 12h4"/></svg>
                    {t("chronik.vergleich")}
                  </span>}
                  <span className="iio-adt-chev">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                  </span>
                </div>
                {marginCell(l)}
              </div>;
            })}
          </section>;
        })}

        {!loading&&chapters.length>0&&(
          <div className="iio-adt-bookend">
            {t("chronik.eintraege",{count:filtered.length})}{filtered.length!==logs.length?" "+t("chronik.gesamtZusatz",{count:logs.length}):""}
          </div>
        )}
      </div>
    </div>

    {/* ── Dossier: Detail als Full-Stage-Overlay ── */}
    {m&&<div className="iio-adt-dossier" role="dialog" aria-modal="true">
      <div className="iio-adt-dhead">
        <button className="iio-adt-back" onClick={()=>setModalEntry(null)} type="button">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m12 19-7-7 7-7"/><path d="M19 12H5"/></svg>
          {t("dossier.zurueck")}{m.id?<span className="eid"> · {t("dossier.eintragNr",{id:m.id})}</span>:null}
        </button>
        <div className="iio-adt-dtitle">
          <span className={"iio-adt-dbar is-"+mSk}/>
          <h2>{adtActionLabel(m,t)}</h2>
        </div>
        <div className="iio-adt-dmeta">
          {formatTime(m.timestamp)}
          {m.userName?` · ${m.userName}`:""}
          {m.instanceName?` · ${m.instanceName}`:""}
          {m.spaceName?` · ${t("dossier.spaceMitName",{name:m.spaceName})}`:""}
        </div>
      </div>
      <div className="iio-adt-dbody">
        <dl className="iio-adt-kv">
          <dt>{t("dossier.zeitstempel")}</dt><dd>{formatTime(m.timestamp)}</dd>
          <dt>{t("dossier.status")}</dt><dd><span className={"iio-status iio-status--"+mSk}>{t(ADT_STATUS_KEY[mSk])}</span></dd>
          <dt>{t("dossier.kategorie")}</dt><dd>{ADT_CAT_KEY[m.category]?t(ADT_CAT_KEY[m.category]):(m.category||"-")}</dd>
          {m.instanceName&&<><dt>{t("dossier.instanz")}</dt><dd>{m.instanceName}</dd></>}
          {m.spaceName&&<><dt>{t("dossier.space")}</dt><dd>{m.spaceName}</dd></>}
          {m.userName&&<><dt>{t("dossier.benutzer")}</dt><dd>{m.userName}</dd></>}
          {(m.meta?.flowName||m.meta?.flowId)&&<><dt>{t("dossier.artefakt")}</dt><dd>{m.meta.flowName||m.meta.flowId}</dd></>}
          {m.meta?.type&&<><dt>{t("dossier.typ")}</dt><dd>{m.meta.type==="iflow"?"iFlow":m.meta.type==="package"?"Package":m.meta.type}</dd></>}
          {m.meta?.sourcePackageId&&<><dt>{t("dossier.quellpaket")}</dt><dd>{m.meta.sourcePackageId}</dd></>}
          {/* m.details ist der festgehaltene Text des Eintrags und bleibt unveraendert. */}
          {m.details&&<><dt>{t("dossier.details")}</dt><dd className="pre">{m.details}</dd></>}
          <dt>{t("dossier.notiz")}</dt>
          <dd className="iio-adt-dnote">
            {noteConfirmId===m.id?noteConfirm(m)
            :noteEditId===m.id?(
              <div className="iio-adt-note-editor is-dossier">
                <textarea autoFocus value={noteText} onChange={e=>setNoteText(e.target.value)}
                  placeholder={t("notiz.platzhalter")}
                  onKeyDown={e=>{if((e.ctrlKey||e.metaKey)&&e.key==="Enter")saveNote(m.id);}}/>
                <div className="iio-adt-note-row">
                  <button className="iio-btn iio-btn--primary iio-btn--sm" disabled={noteSaving} onClick={()=>saveNote(m.id)}>{noteSaving?t("notiz.speichernLaeuft"):t("notiz.speichern")}</button>
                  <button className="iio-btn iio-btn--tertiary iio-btn--sm" onClick={()=>setNoteEditId(null)}>{t("gemeinsam:aktion.abbrechen")}</button>
                  {m.note&&<button className="iio-btn iio-btn--danger iio-btn--sm" style={{marginLeft:"auto"}} disabled={noteSaving} onClick={()=>{setNoteEditId(null);setNoteConfirmId(m.id);}}>{t("notiz.entfernenKnopf")}</button>}
                </div>
              </div>
            ):m.note?(
              <div className="iio-adt-note is-dossier">
                <span className="iio-adt-note-rule"/>
                <div className="iio-adt-note-text">{m.note}</div>
                <div className="iio-adt-note-actions is-visible">
                  <button onClick={()=>startNote(m)}>{t("gemeinsam:aktion.bearbeiten")}</button>
                  <button className="is-danger" onClick={()=>{setNoteEditId(null);setNoteConfirmId(m.id);}}>{t("notiz.entfernen")}</button>
                </div>
              </div>
            ):(
              <button className="iio-adt-note-ghost is-visible" onClick={()=>startNote(m)} type="button">
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M20.24 12.24a6 6 0 0 0-8.49-8.49L5 10.5V19h8.5z"/><line x1="16" y1="8" x2="2" y2="22"/></svg>
                {t("notiz.hinzufuegen")}
              </button>
            )}
          </dd>
        </dl>

        <AuditChangeReport entryId={m.id} reportId={m.meta?.changeReportId} onAuditStatus={applyReportAuditStatus}/>

        {isTransportWithBpmn(m)
          ?<div className="iio-adt-viewer">
            <BpmnDiffViewer entryId={m.id} meta={m.meta} diffCache={diffCache} setDiffCache={setDiffCache}/>
          </div>
          :(!m.details&&!m.meta?.changeReportId&&<div className="iio-adt-nodetails">{t("dossier.keineDetails")}</div>)}
      </div>
    </div>}
  </div>;
};

export default AuditLog;
